import * as React from 'react';
import { useState } from 'react';

import SlButton from '@shoelace-style/shoelace/dist/react/button';
import SlDrawer from '@shoelace-style/shoelace/dist/react/drawer';
import SlTab from '@shoelace-style/shoelace/dist/react/tab';
import SlTabGroup from '@shoelace-style/shoelace/dist/react/tab-group';
import SlTabPanel from '@shoelace-style/shoelace/dist/react/tab-panel';
import { useAtomValue } from 'jotai';

import { pollHistoryAtom } from './pollHistoryAtom';
import { pollRegistry } from './pollRegistry';
import { pollsAtom } from './pollsAtom';
import type { Poll } from './types';

export function PollerDevTools() {
  const [isOpen, setIsOpen] = useState(false);
  const polls = useAtomValue(pollsAtom);
  const pollHistory = useAtomValue(pollHistoryAtom);
  const [, setTick] = useState(0);

  // Force re-render DevTools every second to update timers
  React.useEffect(() => {
    if (!isOpen) return;

    const interval = setInterval(() => {
      setTick((t) => t + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [isOpen]);

  // Only show for dev
  if (!__DEV__) {
    return null;
  }

  const registeredTypes = pollRegistry.getAllTypes();

  const formatDuration = (ms: number) => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return minutes > 0
      ? `${minutes}m ${remainingSeconds}s`
      : `${remainingSeconds}s`;
  };

  const getElapsedTime = (poll: Poll) => {
    return Date.now() - poll.startedAt;
  };

  return (
    <>
      <SlDrawer
        label="Poller Dev Tools"
        placement="bottom"
        open={isOpen}
        onSlAfterHide={() => setIsOpen(false)}
        style={{ '--size': '60vh', zIndex: 999999 } as React.CSSProperties}
      >
        <div className="mb-4">
          <div className="text-sm font-600 mb-3">
            Active Polls: {polls.length} | History: {pollHistory.length}
          </div>
          <div className="text-xs text-grey-800">
            <span className="font-600">Registered Types:</span>{' '}
            {registeredTypes.join(', ') || 'None'}
          </div>
        </div>

        <SlTabGroup>
          <SlTab slot="nav" panel="active">
            Active Polls
          </SlTab>
          <SlTab slot="nav" panel="history">
            Poll History
          </SlTab>

          <SlTabPanel name="active">
            {polls.length === 0 ? (
              <div className="flex items-center justify-center h-64 text-grey-800 border-2 border-dashed border-grey-300 rounded-1">
                No active polls
              </div>
            ) : (
              <div className="border-2 border-grey-300 rounded-2 overflow-hidden shadow-md">
                <SlTabGroup placement="start">
                  {polls.map((poll) => {
                    const statusColor =
                      poll.status === 'completed'
                        ? '[Completed]'
                        : poll.status === 'failed'
                        ? '[Failed]'
                        : '[Pending]';

                    return (
                      <SlTab key={poll.id} slot="nav" panel={poll.id}>
                        <div className="flex flex-col gap-1 py-1">
                          <div className="flex items-center gap-2">
                            <span>{statusColor}</span>
                            <span
                              className="text-xs font-600 truncate"
                              style={{ maxWidth: '150px' }}
                              title={poll.id}
                            >
                              {poll.id}
                            </span>
                          </div>
                          <div className="text-xs text-grey-800 ml-6">
                            {poll.pollCount} polls •{' '}
                            {formatDuration(getElapsedTime(poll))}
                          </div>
                        </div>
                      </SlTab>
                    );
                  })}

                  {polls.map((poll) => (
                    <SlTabPanel key={poll.id} name={poll.id}>
                      <div className="p-4 font-mono text-xs">
                        <div className="flex gap-4">
                          {/* Left Side - Poll Summary */}
                          <div className="w-1/3 space-y-4">
                            <div className="border-2 border-grey-300 rounded-2 overflow-hidden shadow-md">
                              <div className="bg-grey-100 px-3 py-2 border-b-2 border-grey-300">
                                <span className="font-700 text-sm">
                                  Poll Summary
                                </span>
                              </div>
                              <div className="p-3 bg-white space-y-3">
                                <div className="border-b border-grey-200 pb-2">
                                  <div className="text-grey-900 text-xs uppercase mb-1 font-700">
                                    Poll ID
                                  </div>
                                  <div className="font-700 break-all text-xs">
                                    {poll.id}
                                  </div>
                                </div>
                                <div className="border-b border-grey-200 pb-2">
                                  <div className="text-grey-900 text-xs uppercase mb-1 font-700">
                                    Type
                                  </div>
                                  <div>
                                    <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded-1 text-xs font-700">
                                      {poll.type}
                                    </span>
                                  </div>
                                </div>
                                <div className="border-b border-grey-200 pb-2">
                                  <div className="text-grey-900 text-xs uppercase mb-1 font-700">
                                    Status
                                  </div>
                                  <div>
                                    <span
                                      className={`px-2 py-1 rounded-1 text-xs font-700 ${
                                        poll.status === 'completed'
                                          ? 'bg-green-100 text-green-700'
                                          : poll.status === 'failed'
                                          ? 'bg-red-100 text-red-700'
                                          : 'bg-amber-100 text-amber-700'
                                      }`}
                                    >
                                      {poll.status}
                                    </span>
                                  </div>
                                </div>
                                <div className="border-b border-grey-200 pb-2">
                                  <div className="text-grey-900 text-xs uppercase mb-1 font-700">
                                    Polls / Elapsed
                                  </div>
                                  <div className="font-700 text-xs">
                                    {poll.pollCount} polls /{' '}
                                    {formatDuration(getElapsedTime(poll))}
                                  </div>
                                </div>
                                <div className="border-b border-grey-200 pb-2">
                                  <div className="text-grey-900 text-xs uppercase mb-1 font-700">
                                    Started At
                                  </div>
                                  <div className="font-700 text-xs">
                                    {new Date(
                                      poll.startedAt
                                    ).toLocaleTimeString()}
                                  </div>
                                </div>
                                <div className="pb-2">
                                  <div className="text-grey-900 text-xs uppercase mb-1 font-700">
                                    Last Poll
                                  </div>
                                  <div className="font-700 text-xs">
                                    {new Date(
                                      poll.lastPolledAt
                                    ).toLocaleTimeString()}
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Error Section */}
                            {poll.error && (
                              <div className="border-2 border-red-300 rounded-2 overflow-hidden shadow-md">
                                <div className="bg-red-100 px-3 py-2 border-b-2 border-red-300">
                                  <span className="font-700 text-sm text-red-700">
                                    Error
                                  </span>
                                </div>
                                <div className="p-3 bg-red-100">
                                  <div className="text-red-700 break-words text-xs leading-relaxed">
                                    {poll.error}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Right Side - Data */}
                          <div className="w-2/3 space-y-4">
                            {/* Metadata Section */}
                            <div className="border-2 border-grey-300 rounded-2 overflow-hidden shadow-md">
                              <div className="bg-grey-100 px-3 py-2 border-b-2 border-grey-300">
                                <span className="font-700 text-sm">
                                  Metadata
                                </span>
                              </div>
                              <div className="bg-grey-100">
                                <pre className="p-3 overflow-x-auto text-xs leading-normal m-0 font-mono">
                                  <code>
                                    {JSON.stringify(poll.metadata, null, 2)}
                                  </code>
                                </pre>
                              </div>
                            </div>

                            {/* Data Section */}
                            {poll.data && (
                              <div className="border-2 border-grey-300 rounded-2 overflow-hidden shadow-md">
                                <div className="bg-grey-100 px-3 py-2 border-b-2 border-grey-300">
                                  <span className="font-700 text-sm">
                                    Response Data
                                  </span>
                                </div>
                                <div className="bg-grey-100">
                                  <pre className="p-3 overflow-x-auto text-xs leading-normal max-h-64 overflow-y-auto m-0 font-mono">
                                    <code>
                                      {JSON.stringify(poll.data, null, 2)}
                                    </code>
                                  </pre>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </SlTabPanel>
                  ))}
                </SlTabGroup>
              </div>
            )}
          </SlTabPanel>

          <SlTabPanel name="history">
            {pollHistory.length === 0 ? (
              <div className="flex items-center justify-center h-64 text-grey-900 border-2 border-dashed border-grey-300 rounded-1">
                No poll history available
              </div>
            ) : (
              <div className="border-2 border-grey-300 rounded-2 overflow-hidden shadow-md">
                <SlTabGroup placement="start">
                  {pollHistory.map((entry) => {
                    const { poll } = entry;
                    const statusBadgeColor =
                      entry.status === 'completed'
                        ? 'bg-green-100 text-green-700'
                        : entry.status === 'failed'
                        ? 'bg-red-100 text-red-700'
                        : entry.status === 'timeout'
                        ? 'bg-orange-100 text-orange-700'
                        : 'bg-amber-100 text-amber-700';

                    return (
                      <SlTab key={poll.id} slot="nav" panel={poll.id}>
                        <div className="flex flex-col gap-1 py-1">
                          <div className="flex items-center gap-2">
                            <span
                              className={`px-2 py-0.5 rounded-0.5 text-xs font-700 ${statusBadgeColor}`}
                            >
                              {entry.status}
                            </span>
                            <span
                              className="text-xs font-600 truncate"
                              style={{ maxWidth: '120px' }}
                              title={poll.id}
                            >
                              {poll.id}
                            </span>
                          </div>
                          <div className="text-xs text-grey-800 ml-6">
                            {formatDuration(Date.now() - entry.completedAt)} ago
                          </div>
                        </div>
                      </SlTab>
                    );
                  })}

                  {pollHistory.map((entry) => (
                    <SlTabPanel key={entry.poll.id} name={entry.poll.id}>
                      <div className="p-4 font-mono text-xs">
                        <div className="flex gap-4">
                          {/* Left Side - Poll Summary */}
                          <div className="w-1/3 space-y-4">
                            <div className="border-2 border-grey-300 rounded-2 overflow-hidden shadow-md">
                              <div className="bg-grey-100 px-3 py-2 border-b-2 border-grey-300">
                                <span className="font-700 text-sm">
                                  Poll Summary
                                </span>
                              </div>
                              <div className="p-3 bg-white space-y-3">
                                <div className="border-b border-grey-200 pb-2">
                                  <div className="text-grey-900 text-xs uppercase mb-1 font-700">
                                    Poll ID
                                  </div>
                                  <div className="font-700 break-all text-xs">
                                    {entry.poll.id}
                                  </div>
                                </div>
                                <div className="border-b border-grey-200 pb-2">
                                  <div className="text-grey-900 text-xs uppercase mb-1 font-700">
                                    Type
                                  </div>
                                  <div>
                                    <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded-1 text-xs font-700">
                                      {entry.poll.type}
                                    </span>
                                  </div>
                                </div>
                                <div className="border-b border-grey-200 pb-2">
                                  <div className="text-grey-900 text-xs uppercase mb-1 font-700">
                                    Status
                                  </div>
                                  <div>
                                    <span
                                      className={`px-2 py-1 rounded-1 text-xs font-700 ${
                                        entry.status === 'completed'
                                          ? 'bg-green-100 text-green-700'
                                          : entry.status === 'failed'
                                          ? 'bg-red-100 text-red-700'
                                          : entry.status === 'timeout'
                                          ? 'bg-orange-100 text-orange-700'
                                          : 'bg-amber-100 text-amber-700'
                                      }`}
                                    >
                                      {entry.status}
                                    </span>
                                  </div>
                                </div>
                                <div className="border-b border-grey-200 pb-2">
                                  <div className="text-grey-900 text-xs uppercase mb-1 font-700">
                                    Completed At
                                  </div>
                                  <div className="font-700 text-xs">
                                    {new Date(
                                      entry.completedAt
                                    ).toLocaleTimeString()}
                                  </div>
                                </div>
                                <div className="border-b border-grey-200 pb-2">
                                  <div className="text-grey-900 text-xs uppercase mb-1 font-700">
                                    Duration
                                  </div>
                                  <div className="font-700 text-xs">
                                    {formatDuration(
                                      entry.completedAt - entry.poll.startedAt
                                    )}
                                  </div>
                                </div>
                                <div className="border-b border-grey-200 pb-2">
                                  <div className="text-grey-900 text-xs uppercase mb-1 font-700">
                                    Poll Count
                                  </div>
                                  <div className="font-700 text-xs">
                                    {entry.poll.pollCount} polls
                                  </div>
                                </div>
                                <div className="pb-2">
                                  <div className="text-grey-900 text-xs uppercase mb-1 font-700">
                                    Expires At
                                  </div>
                                  <div className="font-700 text-xs">
                                    {entry.expiresAt ? (
                                      <>
                                        {new Date(
                                          entry.expiresAt
                                        ).toLocaleTimeString()}
                                        {entry.expiresAt > Date.now() ? (
                                          <span className="ml-2 font-600 text-blue-600">
                                            (in{' '}
                                            {formatDuration(
                                              entry.expiresAt - Date.now()
                                            )}
                                            )
                                          </span>
                                        ) : (
                                          <span className="ml-2 text-red-600">
                                            (expired)
                                          </span>
                                        )}
                                      </>
                                    ) : (
                                      <span className="text-grey-400">N/A</span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Error Section */}
                            {entry.error && (
                              <div className="border-2 border-red-300 rounded-2 overflow-hidden shadow-md">
                                <div className="bg-red-100 px-3 py-2 border-b-2 border-red-300">
                                  <span className="font-700 text-sm text-red-700">
                                    Error
                                  </span>
                                </div>
                                <div className="p-3 bg-red-100">
                                  <div className="text-red-700 break-words text-xs leading-relaxed">
                                    {entry.error}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Right Side - Data */}
                          <div className="w-2/3 space-y-4">
                            {/* Metadata Section */}
                            <div className="border-2 border-grey-300 rounded-2 overflow-hidden shadow-md">
                              <div className="bg-grey-100 px-3 py-2 border-b-2 border-grey-300">
                                <span className="font-700 text-sm">
                                  Metadata
                                </span>
                              </div>
                              <div className="bg-grey-100">
                                <pre className="p-3 overflow-x-auto text-xs leading-normal m-0 font-mono">
                                  <code>
                                    {JSON.stringify(
                                      entry.poll.metadata,
                                      null,
                                      2
                                    )}
                                  </code>
                                </pre>
                              </div>
                            </div>

                            {/* Data Section */}
                            {entry.poll.data && (
                              <div className="border-2 border-grey-300 rounded-2 overflow-hidden shadow-md">
                                <div className="bg-grey-100 px-3 py-2 border-b-2 border-grey-300">
                                  <span className="font-700 text-sm">
                                    Response Data
                                  </span>
                                </div>
                                <div className="bg-grey-100">
                                  <pre className="p-3 overflow-x-auto text-xs leading-normal max-h-64 overflow-y-auto m-0 font-mono">
                                    <code>
                                      {JSON.stringify(entry.poll.data, null, 2)}
                                    </code>
                                  </pre>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </SlTabPanel>
                  ))}
                </SlTabGroup>
              </div>
            )}
          </SlTabPanel>
        </SlTabGroup>
      </SlDrawer>

      <div className="fixed top-5 -left-4 z-50">
        <SlButton
          onClick={() => setIsOpen(true)}
          style={{ zIndex: 999999 }}
          variant="primary"
          title="Open Poller Dev Tools"
        >
          Polls: {polls.length} | History: {pollHistory.length}
        </SlButton>
      </div>
    </>
  );
}
